import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import Logo from "@/components/Logo";
import { performSignOut } from "@/lib/auth-session";
import { resetPlatformDocumentBranding } from "@/lib/tenant-brand";
import { useTheme } from "@/components/ThemeProvider";
import PlatformErrorBoundary from "@/components/PlatformErrorBoundary";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const nav = [
  { to: "/platform", label: "Overview", end: true },
  { to: "/platform/trial-invites", label: "Trial invites" },
  { to: "/platform/accounts", label: "Accounts" },
  { to: "/platform/billing", label: "Billing" },
  { to: "/platform/billing/settings", label: "Revolut" },
  { to: "/platform/landing", label: "Homepage" },
  { to: "/platform/demo-event", label: "Demo event" },
  { to: "/platform/leads", label: "Leads" },
  { to: "/platform/audit", label: "Audit" },
  { to: "/platform/status", label: "Status" },
  { to: "/platform/settings", label: "Settings" },
];

function PlatformNavLinks({ onNavigate, className = "" }) {
  return (
    <nav className={`flex flex-col gap-1 ${className}`.trim()}>
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-[var(--bg-elev)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function PlatformLayout() {
  const { setTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    resetPlatformDocumentBranding();
    setTheme("dark", false);
  }, [setTheme]);

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <div className="min-h-screen flex min-w-0" style={{ background: "var(--bg-base)" }}>
        <aside
          className="hidden lg:flex w-56 shrink-0 border-r flex-col p-5"
          style={{ borderColor: "var(--border-default)", background: "var(--bg-surface)" }}
        >
          <Link to="/platform" className="mb-8">
            <Logo />
          </Link>
          <div className="text-overline mb-3" style={{ color: "var(--text-tertiary)" }}>
            Platform ops
          </div>
          <PlatformNavLinks />
          <div className="mt-auto pt-6 text-xs text-[var(--text-tertiary)]">
            <p>Customer EMS console:</p>
            <Link to="/admin" className="text-[var(--primary)] hover:underline mt-1 inline-block">
              Open /admin
            </Link>
          </div>
        </aside>

        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent
            side="left"
            className="!w-[min(100vw,18rem)] !p-0 !gap-0 border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)] flex flex-col"
          >
            <SheetTitle className="sr-only">Platform navigation</SheetTitle>
            <div className="p-5 border-b border-[var(--border-default)]">
              <Link to="/platform" onClick={closeMobileNav}>
                <Logo />
              </Link>
              <div className="text-overline mt-4 mb-2" style={{ color: "var(--text-tertiary)" }}>
                Platform ops
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <PlatformNavLinks onNavigate={closeMobileNav} />
            </div>
            <div className="p-5 border-t border-[var(--border-default)] text-xs text-[var(--text-tertiary)]">
              <p>Customer EMS console:</p>
              <Link to="/admin" className="text-[var(--primary)] hover:underline mt-1 inline-block" onClick={closeMobileNav}>
                Open /admin
              </Link>
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex-1 flex flex-col min-w-0">
          <header
            className="h-14 border-b flex items-center justify-between px-4 sm:px-6 shrink-0 gap-3"
            style={{ borderColor: "var(--border-default)" }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                className="btn-ghost !px-2.5 shrink-0 lg:hidden"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation"
                data-testid="platform-mobile-nav-btn"
              >
                <Menu size={18} />
              </button>
              <span className="text-xs font-mono text-[var(--text-tertiary)] truncate">Aivory EMS · operator</span>
            </div>
            <button type="button" className="btn-ghost text-sm shrink-0" onClick={() => performSignOut()}>
              Sign out
            </button>
          </header>
          <main className="flex-1 overflow-auto min-w-0">
            <PlatformErrorBoundary key={location.pathname}>
              <Outlet />
            </PlatformErrorBoundary>
          </main>
        </div>
      </div>
  );
}
